<template>
  <div class="min-h-screen py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-slate-900 dark:text-white">Data Flow & Pipelines</h1>
        <p class="mt-2 text-slate-600 dark:text-slate-400">Monitor API Ingestion and Event Dispatch pipelines.</p>
      </div>

      <div v-if="loading" class="flex justify-center py-20">
         <div class="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
      </div>

      <div v-else>
          <!-- Overview Dashboard -->
          <DataFlowDashboard :overview="overview" />

          <!-- Filters -->
          <div class="flex items-center justify-between mb-6">
              <div class="flex space-x-2">
                  <span class="px-3 py-1 rounded-full bg-slate-200 dark:bg-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-300">All</span>
                  <span class="px-3 py-1 rounded-full text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer">Ingestion</span>
                  <span class="px-3 py-1 rounded-full text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer">Dispatch</span>
              </div>
              
              <div class="relative flex-1 mx-4">
                  <input type="text" placeholder="Search pipelines..." class="w-full pl-8 pr-4 py-1.5 text-sm rounded-lg border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-white focus:ring-indigo-500 focus:border-indigo-500" />
                  <Icon name="heroicons:magnifying-glass" class="absolute left-2.5 top-2 w-4 h-4 text-slate-400" />
              </div>

              <!-- View Toggle -->
              <div class="flex items-center bg-slate-200 dark:bg-slate-700 rounded-lg p-1 border border-slate-300 dark:border-slate-600">
                  <button 
                    @click="viewLayout = 'grid'"
                    class="p-1.5 rounded-md transition-colors"
                    :class="viewLayout === 'grid' ? 'bg-white dark:bg-slate-600 shadow-sm text-indigo-600 dark:text-indigo-400' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'"
                  >
                      <Squares2X2Icon class="w-5 h-5" />
                  </button>
                  <button 
                    @click="viewLayout = 'list'"
                    class="p-1.5 rounded-md transition-colors"
                    :class="viewLayout === 'list' ? 'bg-white dark:bg-slate-600 shadow-sm text-indigo-600 dark:text-indigo-400' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'"
                  >
                      <ListBulletIcon class="w-5 h-5" />
                  </button>
              </div>
          </div>

          <!-- Pipeline Grid -->
          <div 
            :class="viewLayout === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'flex flex-col space-y-4'"
          >
              <PipelineCard 
                v-for="pipeline in pipelines" 
                :key="pipeline.id" 
                :pipeline="pipeline" 
                :layout="viewLayout"
                @view-details="openDrawer"
              />
          </div>
      </div>

      <!-- Detail Drawer -->
      <PipelineDrawer 
        :is-open="drawerOpen" 
        :pipeline="selectedPipeline" 
        @close="drawerOpen = false"
      />

    </div>
  </div>
</template>

<script setup lang="ts">
import DataFlowDashboard from '~/components/dataflow/DataFlowDashboard.vue'
import PipelineCard from '~/components/pipelines/PipelineCard.vue'
import PipelineDrawer from '~/components/pipelines/PipelineDrawer.vue'
import { Squares2X2Icon, ListBulletIcon } from '@heroicons/vue/24/outline'

const loading = ref(true)
const overview = ref<any>(null)
const pipelines = ref<any[]>([])
const viewLayout = ref<'grid' | 'list'>('grid')

// Drawer State
const drawerOpen = ref(false)
const selectedPipeline = ref<any>(null)

const openDrawer = (pipeline: any) => {
    selectedPipeline.value = pipeline
    drawerOpen.value = true
}

const fetchData = async () => {
    // Parallel fetch
    const [{ data: overviewData }, { data: listData }] = await Promise.all([
        useFetch('/api/mocks/pipelines/overview'),
        useFetch('/api/mocks/pipelines/list')
    ])
    
    if (overviewData.value && listData.value) {
        overview.value = overviewData.value
        pipelines.value = listData.value as any[]
        loading.value = false
    }
}

fetchData()
</script>
